import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { tr } from "date-fns/locale";
import { CalendarX, Trash2, Plus, Loader2, CalendarDays } from "lucide-react";
import { toast } from "sonner";

export default function Blockouts() {
  const qc = useQueryClient();
  const { data: blockouts = [], isLoading } = useQuery({ queryKey: ["blockouts"], queryFn: () => api.get("/blockouts").then((r) => r.data) });
  const [form, setForm] = useState({ date: "", full_day: true, start_time: "09:00", end_time: "12:00", reason: "" });
  const [saving, setSaving] = useState(false);

  const add = async () => {
    if (!form.date) { toast.error("Tarih seçin"); return; }
    setSaving(true);
    try {
      await api.post("/blockouts", form);
      qc.invalidateQueries({ queryKey: ["blockouts"] });
      toast.success("Kapalı tarih eklendi");
      setForm({ date: "", full_day: true, start_time: "09:00", end_time: "12:00", reason: "" });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/blockouts/${id}`);
      qc.invalidateQueries({ queryKey: ["blockouts"] });
      toast.success("Silindi");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6" data-testid="blockouts-page">
      <div>
        <h1 className="text-3xl font-bold">Kapalı Tarihler</h1>
        <p className="text-muted-foreground mt-1">Müsait olmayan gün veya saat aralıklarını belirleyin</p>
      </div>

      <div className="rounded-xl border border-border bg-card p-5 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Tarih</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="h-11 mt-1.5 w-full justify-start font-normal" data-testid="blockout-date-input">
                  <CalendarDays className="w-4 h-4 mr-2" />
                  {form.date ? form.date : <span className="text-muted-foreground">Tarih seçin</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" weekStartsOn={1} locale={tr}
                  selected={form.date ? new Date(form.date + "T00:00:00") : undefined}
                  onSelect={(d) => d && setForm((f) => ({ ...f, date: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` }))}
                  disabled={(d) => { const t = new Date(); t.setHours(0, 0, 0, 0); return d < t; }} />
              </PopoverContent>
            </Popover>
          </div>
          <div>
            <Label>Açıklama (opsiyonel)</Label>
            <Input value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} className="h-11 mt-1.5" placeholder="Tatil, izin vb." data-testid="blockout-reason-input" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={form.full_day} onCheckedChange={(v) => setForm((f) => ({ ...f, full_day: v }))} data-testid="blockout-fullday-toggle" />
          <span className="text-sm">Tüm gün kapalı</span>
        </div>
        {!form.full_day && (
          <div className="flex items-center gap-2">
            <Input type="time" value={form.start_time} onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))} className="h-11 w-36" data-testid="blockout-start-input" />
            <span className="text-muted-foreground">-</span>
            <Input type="time" value={form.end_time} onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))} className="h-11 w-36" data-testid="blockout-end-input" />
          </div>
        )}
        <Button onClick={add} disabled={saving} className="rounded-lg" data-testid="add-blockout-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Ekle</>}
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : blockouts.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground" data-testid="no-blockouts">
          Kapalı tarih bulunmuyor.
        </div>
      ) : (
        <div className="space-y-2">
          {blockouts.map((b) => (
            <div key={b.id} className="rounded-xl border border-border bg-card p-4 flex items-center gap-3" data-testid="blockout-item">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center shrink-0">
                <CalendarX className="w-5 h-5 text-red-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold">{b.date}</p>
                <p className="text-sm text-muted-foreground">
                  {b.full_day ? "Tüm gün kapalı" : `${b.start_time} - ${b.end_time}`}{b.reason ? ` · ${b.reason}` : ""}
                </p>
              </div>
              <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove(b.id)} data-testid="delete-blockout-btn">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
